type ValleScreenApiField = {
  id: string;
  label?: string;
  placeholder?: string;
  element?: string; // "input", "select", "textarea", "upload", "plate", etc.
  type?: string; // "text", "number", "date", etc.
  required?: boolean;
  readonly?: boolean;
  helper_text?: string;
  error_text?: string;
  mask?: string | null;
  case?: string;
  maxlength?: number;
  pattern?: string | null;
  autocomplete?: boolean;
  is_PK?: boolean;
  actions?: Array<{ action: string; data?: unknown; endpoint?: string }>;
  visible_screen?: boolean;
  hidden?: boolean;
  value?: unknown;
  description?: string;
  options?: Array<{ value: string; text: string }>;
  upload?: {
    accept?: string;
    multiple?: boolean;
    [key: string]: unknown;
  };
  rows?: number;
  cols?: number;
  [key: string]: unknown;
};

type ValleScreenApiButton = {
  action?: string;
  text?: string;
  label?: string;
  title?: string;
  icon?: string;
  [key: string]: unknown;
};

type ValleScreenApiTab = {
  title?: string;
  form?: string;
  id?: string;
  api?: string | null;
  buttons?: ValleScreenApiButton[];
  lines: ValleScreenApiField[][];
};

type ValleScreenApiJson = {
  title?: string;
  tabs?: ValleScreenApiTab[];
  buttons?: ValleScreenApiButton[];
  lines?: ValleScreenApiField[][];
};

import type { FieldBase as OriginalFieldBase, TabFieldsGroup } from "./Form"; // ajuste o path se necessário
import { z } from "zod";
import type { LicensePlateTheme } from "../LicensePlate/LicensePlate";

// Ensure FieldBase.containerProps is compatible with Record<string, unknown>

import type { FocusEventHandler } from "react";

type FieldBase = Omit<OriginalFieldBase, "containerProps" | "onError" | "mask" | "min" | "max" | "value" | "theme" | "onFocus" | "onBlur"> & {
  containerProps?: Record<string, unknown>;
  onError?: (() => void);
  mask?: string;
  min?: number;
  max?: number;
  value?: string | number | boolean | null | undefined; // Ensure compatibility with FormField
  theme?: LicensePlateTheme | undefined;
  onFocus?: FocusEventHandler<HTMLDivElement>; // Ensure compatibility with FormField
  // onBlur intentionally omitted to match FormField
};

// 1. Defina o schema Zod para os campos da API
const ValleScreenApiFieldSchema = z.object({
  id: z.string(),
  label: z.string().optional(),
  placeholder: z.string().optional(),
  element: z.string().optional(),
  type: z.string().optional(),
  required: z.boolean().optional(),
  readonly: z.boolean().optional(),
  helper_text: z.string().optional(),
  error_text: z.string().optional(),
  mask: z.string().nullable().optional(),
  case: z.string().optional(),
  maxlength: z.number().optional(),
  pattern: z.string().nullable().optional(),
  autocomplete: z.boolean().optional(),
  is_PK: z.boolean().optional(),
  actions: z.array(z.object({
    action: z.string(),
    data: z.unknown().optional(),
    endpoint: z.string().optional(),
  })).optional(),
  visible_screen: z.boolean().optional(),
  hidden: z.boolean().optional(),
  value: z.unknown().optional(),
  description: z.string().optional(),
  options: z.array(z.object({
    value: z.string(),
    text: z.string(),
  })).optional(),
  upload: z.object({
    accept: z.string().optional(),
    multiple: z.boolean().optional(),
  }).passthrough().optional(),
  rows: z.number().optional(),
  cols: z.number().optional(),
}).passthrough();

const ValleScreenApiButtonSchema = z.object({
  action: z.string().optional(),
  text: z.string().optional(),
  label: z.string().optional(),
  title: z.string().optional(),
  icon: z.string().optional(),
}).passthrough();

const ValleScreenApiTabSchema = z.object({
  title: z.string().optional(),
  form: z.string().optional(),
  id: z.string().optional(),
  api: z.string().nullable().optional(),
  buttons: z.array(ValleScreenApiButtonSchema).optional(),
  lines: z.array(z.array(ValleScreenApiFieldSchema)),
});

const ValleScreenApiJsonSchema = z.object({
  title: z.string().optional(),
  tabs: z.array(ValleScreenApiTabSchema).optional(),
  buttons: z.array(ValleScreenApiButtonSchema).optional(),
  lines: z.array(z.array(ValleScreenApiFieldSchema)).optional(),
});

// 2. Use o schema para validar o JSON recebido
export function validateValleScreenApiJson(data: unknown) {
  return ValleScreenApiJsonSchema.safeParse(data);
}

// 3. Use os tipos do FormFields normalmente
// Exemplo de uso:
import type { FormConfig } from "./Form";

export function convertScreensApiToFormConfig(valleJson: unknown): FormConfig {
  const parsed = validateValleScreenApiJson(valleJson);
  if (!parsed.success) {
    throw new Error("JSON inválido para o formulário: " + JSON.stringify(parsed.error.format()));
  }

  // Chame a função convertScreensApiToFormConfigImpl para evitar duplicidade
  return convertScreensApiToFormConfigImpl(parsed.data);
}

function mapValleFieldToFormField(field: ValleScreenApiField): FieldBase {
  // Mapeia tipos do valle para tipos do seu form
  let type: FieldBase["type"] = "input";
  let inputType: FieldBase["inputType"] = "text";

  // Elementos e tipos
  if (field.element === "select") type = "select";
  else if (field.element === "upload") type = "upload";
  else if (field.element === "toggle") type = "toggle";
  else if (field.element === "radiobutton") type = "radiobutton";
  else if (field.element === "textarea") type = "input";
  else if (field.element === "input" && (field.type === "date" || field.type === "datetime")) type = "date";
  else if (field.element === "plate") type = "input"; // você pode criar um campo customizado aqui

  // inputType
  if (type === "input") {
    inputType = field.type || "text";
    if (field.element === "textarea") inputType = undefined;
  }

  // Validações
  const validators: FieldBase["validators"] = [];
  if (field.required) {
    validators.push({
      type: "sync",
      validate: (value) => {
        if (typeof value === "number") return value === 0 || !!value;
        if (typeof value === "string") return value.trim().length > 0;
        return !!value;
      },
      message: field.error_text || "Campo obrigatório"
    });
  }
  if (field.pattern) {
    const regex = new RegExp(field.pattern);
    validators.push({
      type: "sync",
      validate: (value) => regex.test(value as string),
      message: field.error_text || "Valor inválido"
    });
  }
  if (typeof field.maxlength === "number" && field.maxlength > 0) {
    validators.push({
      type: "sync",
      validate: (value) => (value ? ("" + value).length <= field.maxlength! : true),
      message: field.error_text || `Máximo de ${field.maxlength} caracteres`
    });
  }
  // Ações remotas (validação remota ou custom)
  if (Array.isArray(field.actions)) {
    for (const act of field.actions) {
      if (act.action && (act.action.includes("exists") || act.action.includes("find") || act.action.includes("custom"))) {
        validators.push({
          type: "remote",
          endpoint: act.endpoint,
          validate: async (value, formData) => {
            // Exemplo de chamada remota: você deve adaptar o endpoint real
            if (!act.endpoint) return { valid: true };
            const resp = await fetch(act.endpoint, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ value, formData }),
            }).then((r) => r.json());
            return { valid: resp.valid, message: resp.message };
          },
          message: field.error_text || "Valor inválido (remoto)"
        });
      }
    }
  }

  // Opções
  // (options mapping is not used, so this block is removed)

  // Upload props
  let uploadProps: FieldBase["uploadProps"] | undefined = undefined;
  if (type === "upload" && field.upload) {
    uploadProps = {
      accept: field.upload.accept,
      maxFiles: field.upload.multiple ? 10 : 1,
      multiple: field.upload.multiple,
      // outros atributos que você desejar mapear
    };
  }

  // Visibilidade condicional
  const visibleIf = () => {
    if (field.visible_screen === false || field.hidden) return false;
    return true;
  };

  // Campo textarea
  const as = field.element === "textarea" ? "textarea" : undefined;

  // Mapeie outros atributos relevantes
  return {
    id: field.id,
    type,
    inputType,
    label: field.label,
    required: !!field.required,
    disabled: !!field.readonly,
    readOnly: !!field.readonly,
    validators: validators.length ? validators : undefined,
    helperText: field.helper_text,
    errorMessage: field.error_text,
    placeholder: field.placeholder || undefined,
    className: undefined,
    inputClassName: undefined,
    value:
      typeof field.value === "string" ||
      typeof field.value === "number" ||
      typeof field.value === "boolean"
        ? field.value
        : field.value === null
        ? null
        : undefined,
    maxLength: field.maxlength,
    mask: typeof field.mask === "string" ? field.mask : undefined,
    pattern: field.pattern || undefined,
    as,
    visibleIf,
    uploadProps,
    rows: field.rows,
    cols: field.cols,
    autoComplete: field.autocomplete ? "on" : "off",
    // outros campos customizados do valle...
  };
}

type FormButton = {
  title: string;
  action: string;
  icon?: string;
  custom?: boolean;
  // outros atributos caso precise
};

function mapValleButtonsToFormButtons(buttons: ValleScreenApiButton[] | undefined): FormButton[] {
  if (!Array.isArray(buttons)) return [];
  return buttons.map((btn) => ({
    title: btn.text || btn.label || btn.title || "",
    action: btn.action || "",
    icon: btn.icon || undefined,
    custom: btn.action?.includes("custom"),
    // outros atributos caso precise
  }));
}

function convertScreensApiToFormConfigImpl(valleJson: ValleScreenApiJson): FormConfig {
  const title = valleJson.title || "Formulário";

  let fields: (FieldBase[] | TabFieldsGroup<FieldBase>)[] = [];
  let useTabs = false;

  if (Array.isArray(valleJson.tabs) && valleJson.tabs.length > 0) {
    useTabs = true;
    fields = valleJson.tabs.map((tab) => ({
      tabLabel: tab.title || tab.form || tab.id || "Aba",
      fields: ([] as FieldBase[]).concat(...tab.lines.map(line => line.map(mapValleFieldToFormField))),
    }));
  } else if (Array.isArray(valleJson.lines) && valleJson.lines.length > 0) {
    fields = valleJson.lines.map(line => line.map(mapValleFieldToFormField));
  }

  // Botões agrupados por status
  const buttons: FormConfig["buttons"] = {};
  if (useTabs && Array.isArray(valleJson.tabs)) {
    // Se cada aba tiver seus próprios botões, pode agrupar aqui, ou pode usar só da primeira como default
    const tabButtons = valleJson.tabs[0]?.buttons || [];
    buttons.create = mapValleButtonsToFormButtons(tabButtons);
  } else {
    buttons.create = mapValleButtonsToFormButtons(valleJson.buttons);
  }

  return {
    title,
    fields,
    buttons,
    useTabs,
    columns: 1, // padrão, ou detecte pelo JSON se desejar
    // multiStep: true, // pode detectar se quiser
    // outros atributos que desejar mapear
  };
}

export type {
  ValleScreenApiField,
  ValleScreenApiButton,
  ValleScreenApiTab,
  ValleScreenApiJson,
  FieldBase,
};
